// Built-in field descriptors for the Application & Courses field-config tables.
// Each describes a field's default label and input type + picklist/enum-lock
// hints; the live label/visible/required come from the program template's
// `fields` hash and are edited via the FieldGroup component.

export type FieldProvince =
  'PROGRAM' | 'EMPLOYEE' | 'APPLICATION' | 'COURSE' | 'DEPENDENT' | 'SCHOLARSHIP';

export interface FieldDescriptor {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'currency' | 'textarea' | 'select' | 'school';
  /** Field draws from a picklist (tas-field-option) — option editor applies. */
  picklist?: boolean;
  /** Options are company-defined and read-only (e.g. employment status). */
  enumLocked?: boolean;
  /**
   * Field is a plain text/currency input by default but the admin can switch its
   * Type to Dropdown (FieldGroup's Type selector), which sets the field's
   * `value_as_field_option` config flag and enables the option editor. Distinct
   * from `picklist`, which is always a dropdown. Requires `picklist: true` for the
   * option machinery.
   */
  optionalPicklist?: boolean;
  /**
   * Feature gate, not an employee input: only `visible` (shows/hides a whole
   * employee-app section) and the label (reused as that section's heading text)
   * apply. Renders with a "Section toggle" type chip and no Required control.
   */
  gate?: boolean;
  /** Small muted helper note shown under the field's label in the config table. */
  note?: string;
  /** Scope of the field, mirroring the model's `@province` annotation. Used to
   * filter built-ins for tracking-only programs (PROGRAM/EMPLOYEE only). The
   * deprecated APPLICANT province is represented as EMPLOYEE here. */
  province?: FieldProvince;
}

// Province per the model's @province: APPLICATION_ID=PROGRAM; NAME/EMPLOYEE_ID/
// FT_PT_OTHER/EMPLOYEE_EMAIL/HIRE_DATE=APPLICANT(≈EMPLOYEE); EMPLOYEE_DOB/
// STUDENT_ID=APPLICATION (so they drop out for tracking-only programs).
export const EMPLOYEE_FIELDS: FieldDescriptor[] = [
  { name: 'APPLICATION_ID', label: 'Application ID', type: 'text', province: 'PROGRAM' },
  { name: 'NAME', label: 'Employee Name', type: 'text', province: 'EMPLOYEE' },
  { name: 'EMPLOYEE_ID', label: 'Employee ID', type: 'text', province: 'EMPLOYEE' },
  {
    name: 'FT_PT_OTHER',
    label: 'Employment Status (FT/PT)',
    type: 'select',
    picklist: true,
    enumLocked: true,
    province: 'EMPLOYEE',
  },
  { name: 'EMPLOYEE_EMAIL', label: 'Employee Email', type: 'text', province: 'EMPLOYEE' },
  { name: 'HIRE_DATE', label: 'Hire Date', type: 'date', province: 'EMPLOYEE' },
  { name: 'EMPLOYEE_DOB', label: 'Date of Birth', type: 'date', province: 'APPLICATION' },
  { name: 'STUDENT_ID', label: 'Student ID', type: 'text', province: 'APPLICATION' },
];

export const DEPENDENT_FIELDS: FieldDescriptor[] = [
  { name: 'DEPENDENT_NAME', label: 'Dependent Name', type: 'text', province: 'DEPENDENT' },
  { name: 'DEPENDENT_DOB', label: 'Dependent Date of Birth', type: 'date', province: 'DEPENDENT' },
  {
    name: 'DEPENDENT_RELATIONSHIP',
    label: 'Dependent Relationship',
    type: 'select',
    picklist: true,
    province: 'DEPENDENT',
  },
];

// All PROGRAM province per the model (verified) — they all show for tracking-only.
export const PROGRAM_INFO_FIELDS: FieldDescriptor[] = [
  {
    name: 'SCHOOL_INSTITUTION_NAME',
    label: 'School / Institution Name',
    type: 'school',
    province: 'PROGRAM',
  },
  {
    name: 'PROGRAM_SPECIALIZATION',
    label: 'Program Specialization',
    type: 'text',
    picklist: true,
    optionalPicklist: true,
    province: 'PROGRAM',
  },
  {
    name: 'PROGRAM_MAJOR',
    label: 'Program Major',
    type: 'text',
    picklist: true,
    optionalPicklist: true,
    province: 'PROGRAM',
  },
  {
    name: 'ESTIMATED_PROGRAM_BEGIN',
    label: 'Estimated Program Begin',
    type: 'date',
    province: 'PROGRAM',
  },
  {
    name: 'ESTIMATED_PROGRAM_COMPLETION',
    label: 'Estimated Program Completion',
    type: 'date',
    province: 'PROGRAM',
  },
  {
    name: 'ESTIMATED_COST',
    label: 'Estimated Cost',
    type: 'currency',
    picklist: true,
    optionalPicklist: true,
    province: 'PROGRAM',
  },
  {
    name: 'ENROLLMENT_OBJECTIVE',
    label: 'Enrollment Objective',
    type: 'text',
    picklist: true,
    optionalPicklist: true,
    province: 'PROGRAM',
  },
];

// Course fields are split to mirror the employee experience: descriptive details
// (entered when adding a course) vs. the monetary expense line items.
export const COURSE_DETAIL_FIELDS: FieldDescriptor[] = [
  {
    name: 'COURSE_NAME',
    label: 'Course Name',
    type: 'text',
    picklist: true,
    optionalPicklist: true,
  },
  {
    name: 'COURSE_NUMBER',
    label: 'Course Number',
    type: 'text',
    picklist: true,
    optionalPicklist: true,
  },
  {
    name: 'COURSE_DESCRIPTION',
    label: 'Course Description',
    type: 'textarea',
    picklist: true,
    optionalPicklist: true,
  },
  { name: 'COURSE_CREDIT', label: 'Credit Hours', type: 'number' },
  {
    // Paired with Credit Hours (the rate it multiplies), so it lives right
    // after it — same ordering as the classic builder. Hidden by default;
    // credit-based limit capacity forces it visible + required.
    name: 'COST_PER_CREDIT',
    label: 'Cost Per Credit',
    type: 'currency',
  },
  { name: 'COURSES_BEGIN_DATE', label: 'Course Begin Date', type: 'date' },
  { name: 'COURSES_END_DATE', label: 'Course End Date', type: 'date' },
  { name: 'COURSES_RECEIPT_DATE', label: 'Course Receipt Date', type: 'date' },
  {
    name: 'COURSE_GRADE',
    label: 'Course Grade',
    type: 'text',
    picklist: true,
    optionalPicklist: true,
    note: 'Grades are normally configured in the grades section above — a Text field or Dropdown here is a legacy option.',
  },
];

// Monetary cost fields — the "Expenses" the employee itemizes. (Cost Per Credit
// lives in the course details group, right after Credit Hours.)
export const COURSE_EXPENSE_FIELDS: FieldDescriptor[] = [
  { name: 'COURSE_TUITION', label: 'Course Tuition', type: 'currency' },
  { name: 'COURSE_BOOKS', label: 'Books', type: 'currency' },
  { name: 'COURSE_LABS', label: 'Labs', type: 'currency' },
  { name: 'COURSE_SOFTWARE', label: 'Software', type: 'currency' },
  { name: 'COURSE_FEES', label: 'Fees', type: 'currency' },
];

export const SCHOLARSHIP_FIELDS: FieldDescriptor[] = [
  {
    // Not an input — a feature gate. `visible` drives the model's
    // `scholarshipsAreVisible`, which shows/hides the whole scholarships step in
    // the employee app; the label titles the Add Scholarships drawer. The
    // application-side value is the array of scholarships the employee adds, so
    // there is no type/options/required to configure.
    name: 'SCHOLARSHIPS_RECEIVED',
    label: 'Scholarships Received?',
    type: 'text',
    gate: true,
    note: 'Shows the scholarships step to employees; the label titles the drawer.',
  },
  { name: 'SCHOLARSHIP_NAME', label: 'Scholarship Name', type: 'text' },
  { name: 'SCHOLARSHIP_AMOUNT', label: 'Scholarship Amount', type: 'currency' },
  {
    // Not typed by anyone — the approval flow auto-derives it from the course
    // begin date when visible. Admins only choose whether it appears (and what
    // it's labeled), so it renders as a gate row.
    name: 'SEMESTER_CODE',
    label: 'Semester Code',
    type: 'text',
    gate: true,
    note: 'Auto-set from the course begin date; visibility controls whether it appears.',
  },
];

/** Credit-mode forces these course fields required + visible. */
export const CREDIT_REQUIRED_COURSE_FIELDS = ['COURSE_CREDIT', 'COST_PER_CREDIT'];

export const ACADEMIC_GRADES = [
  'A',
  'B',
  'C',
  'D',
  'F',
  'Pass',
  'Fail',
  'Incomplete',
  'Withdrawal',
];
